import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

interface Step {
  label: string;
  description: string;
}

interface WizardStepperProps {
  steps: Step[];
  currentStep: number;
}

const WizardStepper: React.FC<WizardStepperProps> = ({ steps, currentStep }) => {
  const { t } = useTranslation('common');

  return (
    <nav aria-label={t('Wizard progress')} className="px-6 py-4 border-b border-border bg-muted/30">
      <ol className="flex items-center gap-2">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isActive = index === currentStep;
          const isPending = index > currentStep;

          return (
            <li key={index} className="flex items-center gap-2 flex-1">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={`
                    flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold shrink-0 transition-colors
                    ${isCompleted ? 'bg-emerald-500 text-white' : ''}
                    ${isActive ? 'bg-primary text-primary-foreground ring-2 ring-primary/30' : ''}
                    ${isPending ? 'bg-muted text-muted-foreground border border-border' : ''}
                  `}
                >
                  {isCompleted ? <Check className="w-4 h-4" /> : index + 1}
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-medium truncate ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {t(step.label)}
                  </p>
                  <p className="text-xs text-muted-foreground truncate hidden sm:block">
                    {t(step.description)}
                  </p>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-px min-w-[24px] ${isCompleted ? 'bg-emerald-500' : 'bg-border'}`}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default WizardStepper;
